'use client'

import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { CheckCircle2, ImagePlus, X } from 'lucide-react'
import { usePathname } from '@/hooks/usePathname'
import { useTranslations } from '@/hooks/useTranslations'
import { apiFetch } from '@/lib/api'
import { ConnectivityService } from '@/lib/connectivity'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const MAX_EDGE_PX = 1600
const JPEG_QUALITY = 0.85
const MAX_MESSAGE_CHARS = 5000

// Re-encode any picked image (incl. iPhone HEIC) to a bounded JPEG and return
// the bare base64 payload (no data: prefix — the server expects raw base64).
async function reencodeToJpeg(file: File): Promise<string> {
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas 2d context unavailable')
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    const base64 = canvas.toDataURL('image/jpeg', JPEG_QUALITY).split(',')[1]
    if (!base64) throw new Error('empty JPEG encode')
    return base64
  } finally {
    URL.revokeObjectURL(url)
  }
}

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const t = useTranslations('feedbackDialog')
  const pathname = usePathname()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [category, setCategory] = useState<'bug' | 'feedback'>('bug')
  const [message, setMessage] = useState('')
  const [imageBase64, setImageBase64] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(false)
  const [online, setOnline] = useState(true)

  // House connectivity check — navigator.onLine alone is unreliable on native.
  useEffect(() => ConnectivityService.getInstance().subscribe(setOnline), [])

  // Reopening starts a fresh attempt; an error keeps the draft text intact.
  useEffect(() => {
    if (open) {
      setSent(false)
      setError(false)
    }
  }, [open])

  // Brief success confirmation (check zooms in, text follows), then close.
  useEffect(() => {
    if (!sent) return
    const timer = setTimeout(() => onOpenChange(false), 2000)
    return () => clearTimeout(timer)
  }, [sent, onOpenChange])

  const pickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    setImageError(false)
    try {
      setImageBase64(await reencodeToJpeg(file))
    } catch {
      setImageError(true)
    }
  }

  const submit = async () => {
    if (!message.trim() || submitting) return
    setSubmitting(true)
    setError(false)
    try {
      const res = await apiFetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          message: message.trim(),
          imageBase64: imageBase64 ?? undefined,
          context: {
            buildId: import.meta.env.VITE_BUILD_ID || 'dev',
            platform: Capacitor.getPlatform(),
            route: pathname,
            userAgent: navigator.userAgent,
          },
        }),
      })
      if (!res.ok) throw new Error(`feedback failed: ${res.status}`)
      setSent(true)
      setMessage('')
      setImageBase64(null)
      setCategory('bug')
    } catch {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center gap-3 py-8 animate-in fade-in zoom-in-95 duration-300">
            <CheckCircle2 className="h-12 w-12 text-green-600 animate-in zoom-in-50 fade-in duration-500" />
            <p className="text-center text-sm font-medium text-green-700 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-150 fill-mode-backwards">
              {t('success')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={category === 'bug' ? 'default' : 'outline'}
                onClick={() => setCategory('bug')}
              >
                {t('categoryBug')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={category === 'feedback' ? 'default' : 'outline'}
                onClick={() => setCategory('feedback')}
              >
                {t('categoryFeedback')}
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-message">{t('messageLabel')}</Label>
              <Textarea
                id="feedback-message"
                rows={5}
                maxLength={MAX_MESSAGE_CHARS}
                placeholder={t('messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('imageLabel')}</Label>
              {imageBase64 ? (
                <div className="flex items-center gap-3">
                  <img
                    src={`data:image/jpeg;base64,${imageBase64}`}
                    alt=""
                    className="h-16 w-16 rounded object-cover"
                  />
                  <Button type="button" size="sm" variant="ghost" onClick={() => setImageBase64(null)}>
                    <X className="mr-1 h-4 w-4" />
                    {t('imageRemove')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImagePlus className="mr-1 h-4 w-4" />
                  {t('imageSelect')}
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={pickImage}
              />
              {imageError && <p className="text-sm text-destructive">{t('imageError')}</p>}
            </div>

            {error && <p className="text-sm text-destructive">{t('error')}</p>}
            {!online && <p className="text-sm text-muted-foreground">{t('offlineHint')}</p>}

            <Button
              type="button"
              className="w-full"
              disabled={!message.trim() || submitting || !online}
              onClick={submit}
            >
              {submitting ? t('sending') : t('submit')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
